import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { requestsAPI, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Receipt, ArrowLeft, Check, X, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png";

export default function RequestsPage() {
  const { user, refreshUser } = useAuth();
  const [incoming, setIncoming] = useState([]);
  const [outgoing, setOutgoing] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const [inRes, outRes] = await Promise.all([
        requestsAPI.getIncoming(),
        requestsAPI.getOutgoing()
      ]);
      setIncoming(inRes.data);
      setOutgoing(outRes.data);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (requestId) => {
    setProcessingId(requestId);
    try {
      await requestsAPI.accept(requestId);
      await refreshUser();
      await loadRequests();
      toast.success('Request accepted!');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (requestId) => {
    setProcessingId(requestId);
    try {
      await requestsAPI.reject(requestId);
      await loadRequests();
      toast.success('Request rejected');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setProcessingId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return <span className="px-2 py-1 bg-[#00875A]/10 text-[#00875A] text-xs font-semibold rounded-sm">Accepted</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-[#FF3B30]/10 text-[#FF3B30] text-xs font-semibold rounded-sm">Rejected</span>;
      default:
        return <span className="px-2 py-1 bg-[#22C55E]/10 text-[#22C55E] text-xs font-semibold rounded-sm">Pending</span>;
    }
  };

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-[#999999] hover:text-white mb-6" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Link>

        <div className="flex items-center justify-between mb-6 gap-3">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Payment Requests
          </h1>
          <Link to="/request">
            <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm text-sm" data-testid="new-request-button">
              <Receipt className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">New Request</span>
              <span className="sm:hidden">New</span>
            </Button>
          </Link>
        </div>

        <Tabs defaultValue="incoming" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#1A1A1A] border border-[#333333] rounded-sm p-1">
            <TabsTrigger 
              value="incoming" 
              className="rounded-sm data-[state=active]:bg-[#22C55E] data-[state=active]:text-white text-[#999999]"
              data-testid="incoming-tab"
            >
              To Pay ({incoming.filter(r => r.status === 'pending').length})
            </TabsTrigger>
            <TabsTrigger 
              value="outgoing"
              className="rounded-sm data-[state=active]:bg-[#22C55E] data-[state=active]:text-white text-[#999999]"
              data-testid="outgoing-tab"
            >
              Sent ({outgoing.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="incoming">
            {loading ? (
              <div className="text-center py-12 text-[#999999]">Loading...</div>
            ) : incoming.length === 0 ? (
              <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none">
                <CardContent className="p-12 text-center">
                  <Receipt className="w-16 h-16 mx-auto mb-4 text-[#333333]" />
                  <p className="text-[#999999]">No payment requests to pay</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {incoming.map((req) => (
                  <Card 
                    key={req.request_id} 
                    className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none"
                    data-testid={`incoming-request-${req.request_id}`}
                  >
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            {getStatusBadge(req.status)}
                            <span className="text-xs text-[#666666]">{formatDate(req.created_at)}</span>
                          </div>
                          <p className="font-medium text-white truncate">
                            <span className="text-[#999999]">From:</span> {req.to_user_email}
                          </p>
                          {req.note && (
                            <p className="text-sm text-[#666666] mt-2 italic truncate">"{req.note}"</p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
                            {formatCurrency(req.amount)}
                          </p>
                        </div>
                      </div>
                      
                      {req.status === 'pending' && (
                        <div className="flex gap-3 mt-4 pt-4 border-t border-[#333333]">
                          <Button
                            variant="outline"
                            onClick={() => handleReject(req.request_id)}
                            disabled={processingId === req.request_id}
                            className="flex-1 rounded-sm border-[#333333] hover:bg-[#333333] text-white"
                            data-testid={`reject-request-${req.request_id}`}
                          >
                            <X className="w-4 h-4 mr-2" />
                            Decline
                          </Button>
                          <Button
                            onClick={() => handleAccept(req.request_id)}
                            disabled={processingId === req.request_id}
                            className="flex-1 bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm"
                            data-testid={`accept-request-${req.request_id}`}
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Pay {formatCurrency(req.amount)}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="outgoing">
            {loading ? (
              <div className="text-center py-12 text-[#999999]">Loading...</div>
            ) : outgoing.length === 0 ? (
              <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none">
                <CardContent className="p-12 text-center">
                  <Receipt className="w-16 h-16 mx-auto mb-4 text-[#333333]" />
                  <p className="text-[#999999] mb-4">No requests sent yet</p>
                  <Link to="/request">
                    <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm">
                      Send a Request
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {outgoing.map((req) => (
                  <Card 
                    key={req.request_id} 
                    className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none"
                    data-testid={`outgoing-request-${req.request_id}`}
                  >
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            {getStatusBadge(req.status)}
                            <span className="text-xs text-[#666666]">{formatDate(req.created_at)}</span>
                          </div>
                          <p className="font-medium text-white truncate">
                            <span className="text-[#999999]">To:</span> {req.from_user_email}
                          </p>
                          {req.note && (
                            <p className="text-sm text-[#666666] mt-2 italic truncate">"{req.note}"</p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xl sm:text-2xl font-bold text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
                            {formatCurrency(req.amount)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
